import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Animations } from '@core_n/Animations';
import AudioMotionAnalyzer from 'audiomotion-analyzer';
import * as utils from '../../../common/utils';
import { ZIndexMngr } from '../../../common/ZIndexMngr';
import { ISoundPlayer } from './iSoundPlayer';

@Component({
  selector: 'app-soundplayerwindow',
  templateUrl: './soundPlayerWindow.component.html',
  styleUrls: ['./soundPlayerWindow.componant.scss'],
  animations: [
    Animations.WindowOpenCloseFadeHeight,
    Animations.WindowShrink
  ]
})

export class SoundPlayerWindowComponent extends ZIndexMngr implements OnInit, AfterViewInit, OnDestroy {
  static readonly DIALOG_ID = 'PlayerWindow';
  // my sound componant with controls and feeder...
  @ViewChild("PlayerComp") public AudioPlayerComp: ISoundPlayer;
  @ViewChild("spectrogramCanvas") spectrogramCanvasRef!: ElementRef<HTMLCanvasElement>;

  public audioMotion: AudioMotionAnalyzer;
  public AudioPlayer: AudioContext;
  public AudioAnalyzer: AnalyserNode;

  public sensorId: number = 0;
  public eqSwitch: boolean = true;

  public MaxEqPresets = 6;
  public EqPresetIndex = 2; //must start with normal eq before switching to mel spectrogram

  constructor(public dialogRef: MatDialogRef<SoundPlayerWindowComponent>,
    elementRef: ElementRef) {
    super(elementRef);
  }

  ngOnInit() { }

  ngAfterViewInit() {
    setTimeout(() => {
      this.windowVisible.next(true);
    });

    this.audioMotion = new AudioMotionAnalyzer(document.getElementById('container'),
      {
        height: 200,
        mode: 3,
        barSpace: .6,
        ledBars: true,
        useCanvas: true
      });

    this.AudioPlayer = this.audioMotion.audioCtx;

    this.audioMotion.registerGradient('fiery', {
      bgColor: 'black',
      colorStops: [
        { pos: 0.3, color: 'yellow' },
        { pos: 0.7, color: 'orange' },
        { pos: 1, color: 'red' },
      ],
    });

    this.audioMotion.registerGradient('circ', {
      bgColor: 'black',
      colorStops: [
        { pos: 0, color: 'purple' },
        { pos: 0.5, color: 'blue' },
        { pos: 1, color: 'cyan' },
      ],
    });

    this.changeEqPreset(-1);
  }

  ngOnDestroy(): void {
    this.dialogClosed();
    this.dialogRef.close();
    this.dialogRef = null;
  }

  EqSwitch() {
    this.eqSwitch = !this.eqSwitch;
    if (this.eqSwitch) // eq on
      this.audioMotion.start();
    else
      this.audioMotion.stop();
  }

  connectToEq(Source: AudioBufferSourceNode) {
    this.AudioAnalyzer = this.AudioPlayer.createAnalyser();
    this.AudioAnalyzer.fftSize = 2048;
    Source.connect(this.AudioAnalyzer);
    this.AudioAnalyzer.connect(this.AudioPlayer.destination);
    this.audioMotion.connectInput(this.AudioAnalyzer);
  }

  changeEqPreset(Mover: number) {
    this.EqPresetIndex += Mover;
    if (this.EqPresetIndex < 1)
      this.EqPresetIndex = this.MaxEqPresets;
    if (this.EqPresetIndex > this.MaxEqPresets)
      this.EqPresetIndex = 1;

    switch (this.EqPresetIndex) {
      case 2:
        {
          // classic
          this.defaultAudioMotionOptions();
          break;
        }
      case 3:
        {
          // mirrored rainbow
          this.audioMotion.setOptions({
            height: 200,
            mode: 10,
            barSpace: .1,
            ledBars: false,
            gradient: 'rainbow',
            showScaleX: true,
            showScaleY: false,
            reflexRatio: 0.5,
            reflexAlpha: 0.3,
            radial: false,
            showPeaks: true,
            smoothing: 0.5,
            lineWidth: 0,
            channelLayout: 'single',
            fillAlpha: 1,
            frequencyScale: 'log',
            outlineBars: false,
            weightingFilter: '',
            alphaBars: false,
            linearAmplitude: false,
            linearBoost: 1,
            onCanvasDraw: undefined,
            useCanvas: true
          });
          break;
        }
      case 4:
        {
          // fire
          this.audioMotion.setOptions({
            height: 200,
            mode: 10,
            barSpace: .15,
            ledBars: false,
            gradient: 'fiery',
            showScaleX: true,
            showScaleY: false,
            reflexRatio: 0,
            reflexAlpha: 0.15,
            radial: false,
            showPeaks: true,
            smoothing: 0.5,
            lineWidth: 0,
            channelLayout: 'single',
            fillAlpha: 1,
            frequencyScale: 'log',
            outlineBars: false,
            weightingFilter: '',
            alphaBars: false,
            linearAmplitude: false,
            linearBoost: 1,
            onCanvasDraw: undefined,
            useCanvas: true,
          });
          break;
        }
      case 5:
        {
          // circular 
          this.audioMotion.setOptions({
            height: 200,
            mode: 3,
            barSpace: 0.05,
            ledBars: false,
            gradient: 'rainbow',
            showScaleX: false,
            showScaleY: false,
            reflexRatio: 0.7,
            reflexAlpha: 0.4,
            radial: true,
            showPeaks: false,
            smoothing: 0.7,
            lineWidth: 2,
            channelLayout: 'single',
            fillAlpha: 1,
            frequencyScale: 'log',
            outlineBars: false,
            weightingFilter: '',
            alphaBars: false,
            linearAmplitude: false,
            linearBoost: 2.4,
            useCanvas: true,
            onCanvasDraw: instance => {
              instance.radius = .6 + instance.getEnergy() * 1.2;
              instance.spinSpeed = 2;
            }
          });
          break;
        }
      case 6:
        {
          // dual channel 
          this.audioMotion.setOptions({
            height: 200,
            mode: 10,
            channelLayout: 'dual-combined',
            fillAlpha: .3,
            frequencyScale: 'log',
            gradientLeft: 'steelblue',
            gradientRight: 'orangered',
            outlineBars: true,
            weightingFilter: 'D',
            barSpace: .1,
            ledBars: false,
            showScaleX: true,
            showScaleY: false,
            reflexRatio: 0,
            reflexAlpha: 0.15,
            radial: false,
            showPeaks: false,
            smoothing: 0.5,
            lineWidth: 1.5,
            alphaBars: true,
            linearAmplitude: false,
            linearBoost: 1,
            onCanvasDraw: undefined,
            useCanvas: true
          });
          break;
        }
      case 1: {
        this.audioMotion.setOptions({
          useCanvas: false,
          // mode: 10,
          frequencyScale: 'mel',
          showScaleX: false,
          showScaleY: false,
          fillAlpha: 0,
          lineWidth: 0,
          smoothing: 0.65,
          onCanvasDraw: (instance) => {
            const specCanvas = this.spectrogramCanvasRef?.nativeElement;
            
            if(!specCanvas) return;
            
            if(specCanvas.clientWidth <= 0 || specCanvas.clientHeight <= 0) return;

            const specCtx = specCanvas.getContext('2d')
            if(!specCtx) return;

            if(specCanvas.width !== specCanvas.clientWidth || specCanvas.height !== specCanvas.clientHeight){
              specCanvas.width = specCanvas.clientWidth;
              specCanvas.height = specCanvas.clientHeight;
              specCtx.fillStyle = '#000000';
              specCtx.fillRect(0, 0, specCanvas.width, specCanvas.height);
            }

            const width = specCanvas.width; 
            const height = specCanvas.height; 
            const scrollSpeed = 2;

            if(width <= scrollSpeed){
              console.error(`Draw image failed, width (${width}) <= scrollSpeed (${scrollSpeed})`)
              return;
            }

            specCtx.drawImage(
              specCanvas,
              0, 0 , width - scrollSpeed, height,
              scrollSpeed, 0, width - scrollSpeed,height);
              

            const bars = instance.getBars();
            const numBars = bars.length;

            if (numBars > 0){
              const barHeight = height / numBars;
              const xPosition = 0;

              bars.forEach((bar: any, index: number) => {
                const yPosition = height - (index + 1) * barHeight;
                const barValue = Math.max(...bar.value)
                const intensity = Math.floor(barValue * 255);

                let r=0, g =0, b =0;
                if(intensity < 64){
                  r = Math.floor(intensity * 1.2);
                  g = Math.floor(intensity * 0.2);
                  b = Math.floor(intensity * 2.5);
                } else if(intensity < 128){
                  const factor = (intensity - 64)/ 64;
                  r = 76 + Math.floor(factor * 140);
                  g = 12 + Math.floor(factor * 38);
                  b = 160 + Math.floor(factor * 20);
                } else if (intensity < 192){
                  const factor = (intensity - 128)/ 64;
                  r = 216 + Math.floor(factor * 39);
                  g = 50 + Math.floor(factor * 110);
                  b = 180 + Math.floor(factor * 100);
                }
                  else {
                    const factor = (intensity - 192)/ 64;
                    r = 255;
                    g = 160 + Math.floor(factor * 95);
                    b = 80 + Math.floor(factor * 175);
                }
                

                specCtx.fillStyle = `rgb(${r}, ${g}, ${b})`;
                specCtx.fillRect(xPosition, yPosition, scrollSpeed, Math.ceil(barHeight) + 1);

              });

              // specCtx. drawImage(this.spectrogramCanvas, 0 ,0);
            }
             
          },
        });
        break;
      }
    }
  }
  defaultAudioMotionOptions() {
    this.audioMotion.setOptions({
      height: 200,
      mode: 3,
      barSpace: .6,
      ledBars: true,
      gradient: 'classic',
      showScaleX: true,
      showScaleY: false,
      reflexRatio: 0,
      reflexAlpha: 0.15,
      radial: false,
      showPeaks: true,
      smoothing: 0.5,
      lineWidth: 0,
      channelLayout: 'single',
      fillAlpha: 1,
      frequencyScale: 'log',
      outlineBars: false,
      weightingFilter: '',
      alphaBars: false,
      linearAmplitude: false,
      linearBoost: 1,
      onCanvasDraw: undefined,
      useCanvas: true
    });  }

  static createDialogConfig(upperBarHeight: number) {
    return utils.createDialogConfig(false, false, SoundPlayerWindowComponent.DIALOG_ID,
      { top: `200px`, right: `200px` });
  }
}